import React, { cloneElement, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { getBadgeIconForTrend } from './queries';
import styles from './index.module.scss';

type BadgeProps = {
  content?: React.ReactNode;
  variant:
    | 'default'
    | 'success'
    | 'error'
    | 'warning'
    | 'info'
    | 'positive'
    | 'negative'
    | 'neutral'
    | 'subtle';

  outlined?: boolean;
  type: 'notification' | 'dot';
  size?: 'small' | 'medium';
  id?: string;
};

function Badge({
  content = '',
  children,
  variant = 'default',
  outlined = false,
  type = 'notification',
  size = 'medium',
  id,
}: React.PropsWithChildren<BadgeProps>) {
  const targetRef = useRef<HTMLElement | null>(null);
  const badgeRef = useRef<HTMLDivElement | null>(null);

  const [position, setPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    const updatePosition = () => {
      if (targetRef.current && badgeRef.current) {
        const rect = targetRef.current.getBoundingClientRect();
        const badgeRect = badgeRef.current.getBoundingClientRect();
        setPosition({
          top:
            type === 'dot'
              ? rect.top + window.scrollY
              : (rect.height - badgeRect.height) / 2,
          right: badgeRect.width + 4,
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [children, type, content]);

  const badgeClasses = classnames(
    styles.badge,
    styles[`badge__${type}`],
    styles[`badge__${variant}`],
    {
      [styles.badge__outlined]: outlined,
      [styles.badge__content]: content,
      [styles[`badge__dot__${size}`]]: type === 'dot',
    },
  );

  if (!React.isValidElement(children)) {
    return null;
  }

  if (content === null || content === '') {
    return children;
  }

  return (
    <span style={{ position: 'relative', width: 'fit-content' }}>
      {cloneElement(children as React.ReactElement<any>, { ref: targetRef })}
      <div
        className={badgeClasses}
        style={
          type !== 'dot'
            ? {
                position: 'absolute',
                top: `${position.top}px`,
                right: `-${position.right}px`,
              }
            : {}
        }
        ref={badgeRef}
        id={id}
      >
        {getBadgeIconForTrend(variant)}
        {content}
      </div>
    </span>
  );
}

export default Badge;
